import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { useLang } from "@/i18n";
import { ADDRESS, EMAIL, PHONE_DISPLAY, photos, waLink } from "@/data/site";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppIcon } from "@/components/site/Footer";
import { SEO } from "@/components/site/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-ink outline-none transition-all duration-200 placeholder:text-ink-muted/60 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20";

export default function Contatti() {
  const { t } = useLang();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact-messages`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  const cards = [
    { icon: <WhatsAppIcon size={22} />, label: t.contact.whatsapp, value: PHONE_DISPLAY, href: waLink(t.form.waMsg), testid: "contact-whatsapp", external: true },
    { icon: <Phone size={22} />, label: t.contact.phone, value: PHONE_DISPLAY, href: "tel:+393386264991", testid: "contact-phone" },
    { icon: <Mail size={22} />, label: t.contact.email, value: EMAIL, href: `mailto:${EMAIL}`, testid: "contact-email" },
    { icon: <MapPin size={22} />, label: t.contact.where, value: ADDRESS, href: "https://www.openstreetmap.org/?mlat=36.8279&mlon=14.5267#map=17/36.8279/14.5267", testid: "contact-address", external: true },
  ];

  return (
    <>
      <SEO title={`${t.contact.title} | Casa Vacanze Margherita`} description="Contatta Casa Vacanze Margherita: WhatsApp, telefono, email e modulo di contatto. Santa Croce Camerina, Ragusa." path="/contatti" />
      <PageHero image={photos.portaMare} eyebrow={t.contact.title} title={t.contact.heroTitle} testid="contatti-hero" />

      <section className="py-16 sm:py-24" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="mb-12 max-w-2xl text-base leading-relaxed text-ink-muted sm:text-lg">{t.contact.heroSub}</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.07}>
                <a
                  data-testid={c.testid}
                  href={c.href}
                  {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-sand-deep text-terracotta transition-colors duration-300 group-hover:bg-terracotta group-hover:text-sand">
                    {c.icon}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-ink-muted">{c.label}</span>
                  <span className="mt-1 break-all text-sm font-bold text-ink">{c.value}</span>
                </a>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid items-start gap-10 lg:grid-cols-2">
            <Reveal>
              <div className="rounded-3xl border border-border bg-white p-7 shadow-sm sm:p-9">
                <h2 className="font-serif text-2xl font-semibold text-ink sm:text-3xl">{t.contact.formTitle}</h2>
                <form data-testid="contact-form" onSubmit={submit} className="mt-6 grid gap-4">
                  <input data-testid="contact-name-input" required minLength={2} placeholder={t.form.name} value={form.name} onChange={set("name")} className={inputCls} />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input data-testid="contact-email-input" type="email" required placeholder={t.form.email} value={form.email} onChange={set("email")} className={inputCls} />
                    <input data-testid="contact-phone-input" type="tel" placeholder={t.form.phone} value={form.phone} onChange={set("phone")} className={inputCls} />
                  </div>
                  <textarea data-testid="contact-message-input" required minLength={5} rows={5} placeholder={t.form.messagePh} value={form.message} onChange={set("message")} className={inputCls} />
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={sending}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold text-sand shadow-lg shadow-terracotta/25 transition-all duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
                    {sending ? t.form.sending : t.contact.send}
                  </button>
                </form>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="overflow-hidden rounded-3xl border border-border shadow-xl">
                <iframe
                  data-testid="contact-map-iframe"
                  title="Mappa — Casa Vacanze Margherita"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=14.49%2C36.80%2C14.56%2C36.86&layer=mapnik&marker=36.8279%2C14.5267"
                  className="h-[420px] w-full"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
